/**
 * Git server selector component
 */

import { useState, type ReactElement } from 'react';
import { ROUTES } from '@utils/constants';
import { useGitCredentials } from '../hooks/useGitCredentials';
import { useGitServers } from '../hooks/useGitServers';
import type { GitCredential } from '../types/git.types';

interface ServerSelectProps {
  value: string | null;
  onChange: (serverId: string) => void;
  /** When true, only servers the user has or can get a credential for appear. Default true. */
  requireCredential?: boolean;
  /** Render inline as a chip (no own border/background, auto width) for use inside a compound field. */
  compact?: boolean;
}

/**
 * A server the user can use, or can become able to use without an admin
 * touching anything: a stored PAT, or an OAuth app attached to the server
 * (whether or not THIS user has connected to it yet — that's what the
 * Connect prompt in `ProjectSelector`/`GitServerAuthRow` is for). A server
 * with neither (`auth_method === 'none'` and no token) has no path to a
 * credential at all and stays filtered out.
 */
function isUsable(credential: GitCredential | undefined): boolean {
  return Boolean(credential && (credential.has_token || credential.auth_method === 'oauth'));
}

export function ServerSelect({
  value,
  onChange,
  requireCredential = true,
  compact = false,
}: ServerSelectProps): ReactElement {
  const [isOpen, setIsOpen] = useState(false);
  const { data: serversData, isLoading: serversLoading } = useGitServers();
  const { data: credsData, isLoading: credsLoading } = useGitCredentials();

  const triggerClass = compact
    ? 'form-select__trigger form-select__trigger--chip'
    : 'form-select__trigger';

  // Standalone usage needs its own positioned container so the absolute dropdown
  // anchors correctly. In compact mode the parent compound's .form-select
  // provides that anchor, so we render the trigger + dropdown bare.
  const wrap = (children: ReactElement): ReactElement =>
    compact ? children : <div className="form-select">{children}</div>;

  if (serversLoading || credsLoading) {
    return wrap(
      <button type="button" disabled className={triggerClass} aria-label="Git server">
        <span className="form-select__placeholder">Loading servers…</span>
        <span className="form-select__chevron">▼</span>
      </button>,
    );
  }

  const servers = serversData?.servers ?? [];
  const credsByServer = new Map(
    (credsData?.credentials ?? []).map((c) => [c.server_id, c]),
  );

  const visible = requireCredential
    ? servers.filter((s) => isUsable(credsByServer.get(s.id)))
    : servers;

  if (visible.length === 0) {
    if (compact) {
      return (
        <a
          href={ROUTES.SETTINGS}
          className="form-select__trigger form-select__trigger--chip form-select__trigger--link"
        >
          Configure server
        </a>
      );
    }
    return (
      <div className="form-select__empty">
        No servers available.{' '}
        <a href={ROUTES.SETTINGS}>Configure a credential in Settings</a>.
      </div>
    );
  }

  const selected = visible.find((s) => s.id === value) ?? null;

  const handleSelect = (serverId: string): void => {
    onChange(serverId);
    setIsOpen(false);
  };

  return wrap(
    <>
      <button
        type="button"
        onClick={() => { setIsOpen(!isOpen); }}
        className={triggerClass}
        aria-label="Git server"
      >
        {selected ? (
          <span className="form-select__value">
            {compact
              ? selected.display_name
              : `${selected.display_name} (${selected.provider_type})`}
          </span>
        ) : (
          <span className="form-select__placeholder">Select a server</span>
        )}
        <span className="form-select__chevron">{isOpen ? '▲' : '▼'}</span>
      </button>

      {isOpen && (
        <div className="form-select__dropdown">
          {visible.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => { handleSelect(s.id); }}
              className="form-select__option"
            >
              <div className="form-select__option-text">
                <span>{s.display_name}</span>
                <span className="form-select__option-sub">{s.provider_type}</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </>,
  );
}
